import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formsApi } from '@/api/forms';
import { queryKeys } from '@/lib/queryKeys';
import { ApiError } from '@/api/client';

export function FormCreatePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => formsApi.create(input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.forms.all });
      navigate(`/forms/${data.form.id}`);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Falha ao criar formulario');
    },
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    mutation.mutate({
      name: fd.get('name'),
      slug: fd.get('slug') || undefined,
      allowedDomains: String(fd.get('allowedDomains') || '*').split(',').map((s) => s.trim()).filter(Boolean),
      notificationEmails: String(fd.get('notificationEmails') || '').split(',').map((s) => s.trim()).filter(Boolean),
      submissionBehavior: fd.get('submissionBehavior'),
      redirectUrl: fd.get('redirectUrl') || null,
      thankYouMessage: fd.get('thankYouMessage') || null,
      webhookUrl: fd.get('webhookUrl') || null,
      honeypotField: fd.get('honeypotField') || '_gotcha',
    });
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Criar Formulario</h1>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Formulario *</label>
          <input name="name" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Formulario de Contato" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Slug (gerado automaticamente se vazio)</label>
          <input name="slug" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="formulario-de-contato" pattern="[a-z0-9]+(-[a-z0-9]+)*" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dominios Permitidos (separados por virgula, * = todos)</label>
          <input name="allowedDomains" defaultValue="*" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">E-mails de Notificacao (separados por virgula)</label>
          <input name="notificationEmails" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="you@example.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Apos Envio</label>
          <select name="submissionBehavior" defaultValue="thank_you_page" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50">
            <option value="thank_you_page">Mostrar Pagina de Agradecimento</option>
            <option value="redirect">Redirecionar para URL</option>
            <option value="json">Apenas Resposta JSON</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL de Redirecionamento</label>
          <input name="redirectUrl" type="url" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="https://mysite.com/thanks" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de Agradecimento</label>
          <textarea name="thankYouMessage" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Obrigado pelo contato!" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL do Webhook</label>
          <input name="webhookUrl" type="url" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Campo Honeypot</label>
          <input name="honeypotField" defaultValue="_gotcha" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>

        <button type="submit" disabled={mutation.isPending}
          className="w-full py-2 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50">
          {mutation.isPending ? 'Criando...' : 'Criar Formulario'}
        </button>
      </form>
    </div>
  );
}
